import { useQuery } from '@tanstack/react-query'
import { keys } from '../../api/keys'
import { api } from '../../api/client'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { toTxn, type Txn } from './transactionWire'
import { TransactionsError } from './useTransactions'

/** `YYYY-MM-DD`, a calendar date. */
type LocalDate = string

/** The server's cap (`page_size le=100`). One page is the whole answer for a ±15-day window on one account. */
const PAGE_SIZE = 100

/**
 * M6's exported range read: one account's transactions between two calendar
 * dates, both inclusive. M7's payment picker is its consumer — the only thing
 * M7 imports from this module besides the `Txn` type.
 *
 * One page, never paged: a month of one account fits under the server's cap in
 * every realistic case, and a picker that silently misses row 101 is still a
 * picker with a usable cash fallback beside it.
 *
 * Under `keys.transactions.list(...)`, so any transaction write invalidates it.
 * Live (no `asOf`), and zero rows is `empty` — the caller words it.
 */
export function useTransactionsBetween(q: { accountId: string; from: LocalDate; to: LocalDate }): AsyncState<Txn[]> {
  const query = { account_id: q.accountId, start_date: q.from, end_date: q.to, page: 1, page_size: PAGE_SIZE }
  const result = useQuery({
    queryKey: keys.transactions.list(query),
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/v1/transactions', { params: { query }, signal })
      if (error !== undefined || data === undefined) throw new TransactionsError(error, response.status)
      return data.transactions.map(toTxn)
    },
  })
  return fromQuery(result, {
    message: () => 'Transactions could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void result.refetch() },
    isEmpty: (rows) => rows.length === 0,
  })
}
